import React, { createContext, useCallback, useContext, useEffect, useState } from "react"
import styled from "styled-components"
import Icon from "../Icon"
import WhiteSpace from "../components/Whitespace"
import Modal from "../components/Modal"
import { setNotificationHandler } from "./notificationService"
import { BREAKPOINTS, Z } from "../GlobalTheme"

const ToastStack = styled.div`
  position: fixed;
  /* The shell's own top-right controls (theme, language, signout) sit at top: 20px. */
  top: calc(max(20px, env(safe-area-inset-top)) + 56px);
  right: max(20px, env(safe-area-inset-right));
  left: max(20px, env(safe-area-inset-left));
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  z-index: ${Z.toast};
  pointer-events: none;

  /* On a phone the whole top band is the shell header plus, on charger pages, the tab strip;
     anchoring a toast there covers navigation. Bottom is clear and is the platform convention. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    top: auto;
    bottom: calc(12px + env(safe-area-inset-bottom));
    align-items: stretch;
  }
`

const Container = styled.div<{ $level: "normal" | "warning" | "error" }>`
  z-index: ${Z.toast};
  display: flex;
  padding: 8px;
  padding-left: 16px;
  padding-right: 16px;
  border-radius: 32px;
  background-color: ${props => props.theme.colors.white};
  border: 2px solid ${props => props.$level == "warning" ? props.theme.colors.warning : props.$level == "error" ? props.theme.colors.danger : props.theme.colors.primary};
  box-shadow: ${({ theme }) => `0 0 8px ${theme.colors.gray_400}88`};
  align-self: flex-end;
  max-width: 100%;
  flex-wrap: wrap;
  min-width: 0px;
  box-sizing: border-box;
  pointer-events: auto;
  overflow-wrap: anywhere;
`

const Label = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
`

const ButtonContainer = styled.div`
  width: 18px;
  height: 18px;
  cursor: pointer;

  &:active {
    transform: scale(.9);
  }
`

/* ToastStack is pointer-events: none so a toast never blocks the page behind it, and that inherits
   into everything inside the stack - including this modal, which left its close and copy buttons
   inert and dropped clicks through to the page underneath. display: contents generates no box, so
   the toast column keeps its exact layout and only the modal becomes clickable again. */
const JsonModalLayer = styled.div`
  display: contents;
  pointer-events: auto;
`

const ModalCopyButtonContainer = styled.div`
  width: 16px;
  height: 16px;
  padding: 8px;
  background-color: ${props => props.theme.colors.white};
  border-radius: 12px;
  position: absolute;
  right: -40px;
  top: 0px;
  cursor: pointer;

  /* right: -40px sits outside the panel, which is off-screen once the panel is nearly viewport-wide.
     Brought inside, but it has to sit BESIDE the close button at right: 16px rather than on top of
     it - a 32px box at right: 8px covers the 20px X completely and swallows every tap on it. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    right: 48px;
    top: 8px;
    z-index: 1;
  }

  &:active {
    transform: scale(.9);
  }
`

const ModalCloseButtonContainer = styled.div`
  width: 20px;
  height: 20px;
  cursor: pointer;
  position: absolute;
  top: 16px;
  right: 16px;
`

const JsonText = styled.pre`
  padding: 32px;
  max-height: 80dvh;
  overflow-y: scroll;
  overflow-x: auto;
  max-width: 90vw;
  box-sizing: border-box;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    padding: 16px;
    max-width: calc(100vw - 24px);
    max-height: 70dvh;
  }

  .string { color: green; }
  .number { color: darkorange; }
  .boolean { color: blue; }
  .null { color: magenta; }
  .key { color: darkred; }
`

type Notification = {
  id: string
  message: string
  type: "JSON" | "TEXT"
  level?: "normal" | "warning" | "error"
  duration?: number
}

type NotificationContextType = {
  addNotification: (message: string, type: "JSON" | "TEXT", level?: "normal" | "warning" | "error", duration?: number) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const useNotification = () => {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error("useNotification must be inside NotificationProvider")
  return ctx
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<{ value: Notification, isShowingJson: boolean }[]>([])

  const addNotification = useCallback((message: string, type: "JSON" | "TEXT", level?: "normal" | "warning" | "error", duration = 10000) => {
    const id = Math.random().toString(36).substring(2, 9)
    const newNotification = { id, message, type, level, duration }
    setNotifications((prev) => [{ value: newNotification, isShowingJson: false }, ...prev])

    setTimeout(() => {
      setNotifications((prev) => prev.filter((t) => t.value.id !== id || t.isShowingJson === true))
    }, duration)
  }, [])

  useEffect(() => {
    setNotificationHandler(addNotification)
    return () => setNotificationHandler(null)
  }, [addNotification])

  return (
    <NotificationContext.Provider value={{ addNotification: addNotification }}>
      {children}
      <ToastStack>
        {notifications.map((notification) => <React.Fragment key={JSON.stringify(notification)}>
          <Container $level={notification.value.level || "normal"}>
            <Label>{notification.value.message.length > 100 ? `${notification.value.message.slice(0, 100)}...` : notification.value.message}</Label>
            <WhiteSpace width="24px" />
            <ButtonContainer onClick={() => navigator.clipboard.writeText(notification.value.message)}>
              <Icon path="general.copy" width="100%" height="100%" />
            </ButtonContainer>
            {notification.value.type === "JSON" && <>
              <WhiteSpace width="16px" />
              <ButtonContainer onClick={() => setNotifications((prev) => prev.map((t) => t.value.id === notification.value.id ? { ...t, isShowingJson: true } : t))}>
                <Icon path="notification.json" width="100%" height="100%" />
              </ButtonContainer>
            </>}
            <WhiteSpace width="16px" />
            <ButtonContainer onClick={() => setNotifications((prev) => prev.filter((t) => t.value.id !== notification.value.id))}>
              <Icon path="general.x" width="100%" height="100%" />
            </ButtonContainer>
          </Container>
          {notification.isShowingJson && (
            <JsonModalLayer>
            <Modal onEscape={() => setNotifications((prev) => prev.filter((t) => t.value.id !== notification.value.id))}>
              <ModalCloseButtonContainer onClick={() => setNotifications((prev) => prev.filter((t) => t.value.id !== notification.value.id))}>
                <Icon path="general.x" width="100%" height="100%" />
              </ModalCloseButtonContainer>
              <ModalCopyButtonContainer onClick={() => navigator.clipboard.writeText(JSON.stringify(JSON.parse(notification.value.message), undefined, 2))}>
                <Icon path="general.copy" width="100%" height="100%" />
              </ModalCopyButtonContainer>
              <JsonText dangerouslySetInnerHTML={{ __html: jsonSyntaxHighlight(notification.value.message) }} />
            </Modal>
            </JsonModalLayer>
          )}
        </React.Fragment>)}
      </ToastStack>
    </NotificationContext.Provider>
  )
}


function jsonSyntaxHighlight(json: string) {
  if (!json) return "" //no JSON from response

  try {
    json = JSON.stringify(JSON.parse(json), undefined, 2)

    json = json
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
    return json.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
      function (match) {
        let cls = "number"
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = "key"
          } else {
            cls = "string"
          }
        } else if (/true|false/.test(match)) {
          cls = "boolean"
        } else if (/null/.test(match)) {
          cls = "null"
        }
        return '<span class="' + cls + '">' + match + "</span>"
      }
    )
  } catch { }
  return ''
}
